<template>
  <v-card id="contract-sort-scale-summary">
    <v-card-title class="primary white--text">
      <v-icon color="tertiary" class="mr-2">mdi-ruler</v-icon>
      <span class="headline text-truncate">{{ title }}</span>
      <v-spacer/>
      <BaseDialogActions hideRefresh/>
    </v-card-title>
    <v-card-text class="pt-4">
      <v-row dense>
        <v-col cols="12" md="5">
          <v-card outlined class="pa-3 fill-height">
            <div class="overline text--secondary">{{ $t('contractInformation') }}</div>
            <v-row dense class="mt-1">
              <v-col cols="12" sm="6" v-for="info in contractInformation" :key="info.icon">
                <v-row no-gutters align="center">
                  <v-icon small class="mr-2">{{ info.icon }}</v-icon>
                  <span class="body-2 text-truncate">{{ info.value }}</span>
                </v-row>
              </v-col>
            </v-row>
          </v-card>
        </v-col>
        <v-col cols="12" md="7">
          <v-card outlined class="pa-3 fill-height">
            <div class="overline text--secondary">{{ $t('totals') }}</div>
            <v-row dense class="mt-1">
              <v-col cols="6" md="4" v-for="total in totalFigures" :key="total.label">
                <span class="body-2 text--secondary">{{ total.label }}: </span>
                <span class="body-2 font-weight-medium">{{ total.value }}</span>
              </v-col>
            </v-row>
          </v-card>
        </v-col>
      </v-row>
      <v-data-table
      dense
      hide-default-footer
      sort-by="share"
      sort-desc
      item-key="key"
      class="mt-4 small-headers sort-ledger"
      :headers="headers"
      :items="ledgerItems"
      :loading="scaleSummaryLoading"
      :items-per-page="-1"
      :no-data-text="$t('noDataForSelection')"
      :item-class="itemClass"
      :custom-sort="customSort"
      @click:row="rowClicked">
        <template #header.activity="{ header }">
          <div class="activity-name" :class="{ 'hidden-label': !anyRowExpanded }">
            <span>{{ header.text }}</span>
          </div>
        </template>
        <template #item.label="{ item }">
          <template v-if="item.isSortRow">
            <v-icon small color="primary">{{ isExpanded(item.key) ? 'mdi-chevron-down' : 'mdi-chevron-right' }}</v-icon>
            <span class="font-weight-medium">{{ item.label }}</span>
            <span class="caption text--secondary ml-1">· {{ $t('activityLineCount', { count: item.activityLines.length }) }}</span>
          </template>
        </template>
        <template #item.share="{ item }">
          <div v-if="item.isSortRow" class="d-flex align-center">
            <v-progress-linear rounded height="6" color="primary" class="share-bar" :value="item.share"/>
            <span class="caption ml-2">{{ formatPercentage(item.share) }}</span>
          </div>
        </template>
        <template #item.activity="{ item }">
          <div v-if="!item.isSortRow" class="activity-name text-truncate" :title="item.activityName">{{ item.activityName ?? $t('notAvailable') }}</div>
        </template>
        <template #item.rate="{ item }">{{ item.isSortRow ? effectiveRateStringFor(item) : formatMoney(item.rate) }}</template>
        <template #item.grossVolume="{ item }">
          <span :class="{ 'text--secondary': !item.isSortRow }">{{ mbfStringFromBf(item.grossVolume) }}</span>
        </template>
        <template #item.netVolume="{ item }">
          <span :class="item.isSortRow ? 'font-weight-medium' : 'text--secondary'">{{ mbfStringFromBf(item.netVolume) }}</span>
        </template>
        <template #item.amount="{ item }">
          <span :class="{ 'font-weight-medium': item.isSortRow }">{{ formatMoney(item.amount) }}</span>
        </template>
        <template #body.append v-if="sortRows.length > 0">
          <tr class="total-row">
            <td colspan="4" class="font-weight-medium">{{ $t('total') }}</td>
            <td class="text-right font-weight-medium">{{ mbfStringFromBf(totals.grossVolume) }}</td>
            <td class="text-right font-weight-medium">{{ mbfStringFromBf(totals.netVolume) }}</td>
            <td class="text-right font-weight-medium">{{ formatMoney(totals.amount) }}</td>
          </tr>
        </template>
      </v-data-table>
      <div class="caption text--secondary font-italic mt-2">{{ $t('sortLedgerHint') }}</div>
      <v-row no-gutters align="center" class="mt-3">
        <v-col cols="auto">
          <v-switch
          dense
          hide-details
          color="primary"
          class="mt-0"
          v-model="showTickets"
          :label="$t('showTicketSummaries')"/>
        </v-col>
      </v-row>
      <ContractSortTicketSummaries v-if="showTickets" :tickets="tickets" class="mt-4" @ticket-selected="ticketSelected"/>
    </v-card-text>
    <Dialog :stateId="ticketDetailDialogId" @close="focusedTicketId = undefined">
      <TicketDetails
      v-if="focusedTicketId !== undefined"
      :ticketId="focusedTicketId"
      :showCrud="false"
      initialTab="scaling"
      @close="closeTicketDetail"/>
    </Dialog>
  </v-card>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { sortKeyFor, sortLabelFor } from '@/utils/SortDimensions'
import { formatMoney, formatPercentage, mbfStringFromBf, numberWithCommas, effectiveRatePerMbf } from '@/utils/NumericMutations'
import { uniqueDialogId } from '@/utils/componentHelpers'

const SORT_VALUE_FOR = {
  label: row => row.label,
  share: row => row.netVolume,
  rate: row => effectiveRatePerMbf(row.amount, row.netVolume) ?? Number.NEGATIVE_INFINITY,
  grossVolume: row => row.grossVolume,
  netVolume: row => row.netVolume,
  amount: row => row.amount
}

export default {
  name: 'ContractSortScaleSummary',

  components: {
    BaseDialogActions: () => import('@/components/core/BaseDialogActions.vue'),
    ContractSortTicketSummaries: () => import('@/components/ticket/sort-inspector/ContractSortTicketSummaries.vue'),
    Dialog: () => import('@/components/Dialog.vue'),
    TicketDetails: () => import('@/components/ticket/ticket-details/TicketDetails.vue')
  },

  props: {
    contract: {
      type: Object,
      required: true
    },
    destinationName: {
      type: String,
      required: true
    },
    accountName: {
      type: String,
      required: false,
      default: undefined
    },
    settingName: {
      type: String,
      required: false,
      default: undefined
    }
  },

  data: () => ({
    summary: undefined,
    showTickets: false,
    expandedKeys: [],
    focusedTicketId: undefined,
    ticketDetailDialogId: uniqueDialogId('sort-inspector-ticket-detail')
  }),

  computed: {
    ...mapGetters('sort-inspector', ['scaleSummaryLoading']),

    title () {
      const tract = this.contract.tractName
      return `${this.$t('scaleSummaryForContract', { tract })} · ${this.destinationName}`
    },

    tickets () {
      return this.summary?.tickets ?? []
    },

    contractInformation () {
      return [
        { icon: 'mdi-crosshairs-gps', value: this.contract.tractName },
        { icon: 'mdi-account', value: this.accountName },
        { icon: 'mdi-domain', value: this.destinationName },
        { icon: 'mdi-adjust', value: this.settingName }
      ]
    },

    anyRowExpanded () {
      return this.expandedKeys.length > 0
    },

    headers () {
      return [
        { text: this.$t('sort'), value: 'label' },
        { text: this.$t('shareOfNetVolume'), value: 'share', class: 'share-column' },
        { text: this.$t('activity'), value: 'activity', sortable: false },
        { text: this.$t('rate'), value: 'rate', align: 'right' },
        { text: this.$t('grossMbf'), value: 'grossVolume', align: 'right' },
        { text: this.$t('netMbf'), value: 'netVolume', align: 'right' },
        { text: this.$t('amount'), value: 'amount', align: 'right' }
      ]
    },

    totals () {
      const tickets = this.summary?.tickets ?? []
      const activityLines = this.summary?.activityLines ?? []

      const netVolume = tickets.reduce((total, t) => total + (t.netVolume ?? 0), 0)
      const amount = activityLines.reduce((total, line) => total + (line.amount ?? 0), 0)

      return {
        loadCount: tickets.length,
        netTons: tickets.reduce((total, t) => total + (t.netTons ?? 0), 0),
        grossVolume: tickets.reduce((total, t) => total + (t.grossVolume ?? 0), 0),
        netVolume,
        amount,
        averageRate: effectiveRatePerMbf(amount, netVolume)
      }
    },

    totalFigures () {
      return [
        { label: this.$t('payout'), value: formatMoney(this.totals.amount) },
        { label: this.$t('loads'), value: numberWithCommas(this.totals.loadCount) },
        { label: this.$t('netTons'), value: numberWithCommas(this.totals.netTons, 3) },
        { label: this.$t('grossVolume'), value: `${mbfStringFromBf(this.totals.grossVolume)} MBF` },
        { label: this.$t('netVolume'), value: `${mbfStringFromBf(this.totals.netVolume)} MBF` },
        { label: this.$t('averageRate'), value: this.totals.averageRate === null ? this.$t('notAvailable') : `${formatMoney(this.totals.averageRate)} /MBF` }
      ]
    },

    sortRows () {
      const rows = new Map()

      const rowFor = line => {
        const key = sortKeyFor(line)
        if (!rows.has(key)) {
          rows.set(key, {
            key,
            label: sortLabelFor(line, this.$t('unsorted')),
            grossVolume: 0,
            netVolume: 0,
            amount: 0,
            activityLines: []
          })
        }
        return rows.get(key)
      }

      for (const ticket of this.summary?.tickets ?? []) {
        for (const sort of ticket.sorts) {
          const row = rowFor(sort)
          row.grossVolume += sort.grossVolume
          row.netVolume += sort.netVolume
        }
      }

      for (const line of this.summary?.activityLines ?? []) {
        const row = rowFor(line)
        row.amount += line.amount
        row.activityLines.push(line)
      }

      return [...rows.values()]
        .sort((a, b) => b.netVolume - a.netVolume)
        .map(row => ({ ...row, share: this.shareFor(row) }))
    },

    ledgerItems () {
      return this.sortRows.flatMap(row => {
        const parent = { ...row, isSortRow: true }
        if (!this.isExpanded(row.key)) return [parent]

        return [parent, ...row.activityLines.map(line => ({
          ...line,
          key: `${row.key}-${line.activityId}-${line.rateSheetLineId}`,
          parentKey: row.key,
          isSortRow: false
        }))]
      })
    }
  },

  async created () {
    this.summary = await this.fetchContractSortScaleSummary(this.contract.contractId)
  },

  methods: {
    ...mapActions('sort-inspector', ['fetchContractSortScaleSummary']),
    ...mapActions('dialog', ['openOrUpdateDialog', 'closeDialogsAtOrAbove']),
    formatMoney,
    formatPercentage,
    mbfStringFromBf,

    ticketSelected (ticket) {
      this.focusedTicketId = ticket.ticketId
      this.openOrUpdateDialog({ id: this.ticketDetailDialogId, width: '80vw' })
    },

    closeTicketDetail () {
      this.closeDialogsAtOrAbove(this.ticketDetailDialogId)
      this.focusedTicketId = undefined
    },

    customSort (items, sortBy, sortDesc) {
      const valueFor = SORT_VALUE_FOR[sortBy[0]]
      if (valueFor === undefined) return items

      const direction = sortDesc[0] ? -1 : 1
      const linesByParent = new Map()

      for (const line of items.filter(item => !item.isSortRow)) {
        linesByParent.set(line.parentKey, [...(linesByParent.get(line.parentKey) ?? []), line])
      }

      const parents = items.filter(item => item.isSortRow).sort((a, b) => {
        const aValue = valueFor(a)
        const bValue = valueFor(b)
        const comparison = typeof aValue === 'string' ? aValue.localeCompare(bValue) : aValue - bValue
        return comparison * direction
      })

      return parents.flatMap(parent => [parent, ...(linesByParent.get(parent.key) ?? [])])
    },

    rowClicked (item) {
      if (item.isSortRow) this.toggleRow(item.key)
    },

    itemClass (item) {
      return item.isSortRow ? 'sort-row' : 'activity-line-row'
    },

    isExpanded (key) {
      return this.expandedKeys.includes(key)
    },

    toggleRow (key) {
      this.expandedKeys = this.isExpanded(key)
        ? this.expandedKeys.filter(expanded => expanded !== key)
        : [...this.expandedKeys, key]
    },

    effectiveRateStringFor ({ amount, netVolume }) {
      const rate = effectiveRatePerMbf(amount, netVolume)
      return rate === null ? this.$t('notAvailable') : formatMoney(rate)
    },

    shareFor ({ netVolume }) {
      if (!this.totals.netVolume) return 0
      return Math.round(netVolume / this.totals.netVolume * 10000) / 100
    }
  }
}
</script>

<style scoped>
.sort-ledger ::v-deep th {
  white-space: nowrap;
}

.sort-ledger ::v-deep th.share-column {
  width: 25%;
}

.share-bar {
  flex: 0 0 300px;
}

.hidden-label {
  visibility: hidden;
}

.activity-name {
  width: 220px;
}

.sort-ledger ::v-deep tbody > tr.sort-row {
  cursor: pointer;
  background: rgba(209, 95, 39, .06);
}

.sort-ledger ::v-deep tbody > tr.sort-row:hover {
  background: rgba(209, 95, 39, .13) !important;
}

.sort-ledger ::v-deep tbody > tr.sort-row > td:first-child {
  border-left: 4px solid var(--v-primary-base);
  white-space: nowrap;
}

.sort-ledger ::v-deep tbody > tr.activity-line-row {
  white-space: nowrap;
}

.total-row td {
  border-top: 2px solid rgba(0, 0, 0, .87);
}
</style>
